import React, { createContext, useContext, useState, useCallback } from "react";
import * as api from "../services/api";

const HotelContext = createContext();

export const HotelProvider = ({ children }) => {
  const [hotelId, setHotelId] = useState(null);
  const [hotelData, setHotelData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const initializeDraft = useCallback(async (initialData = {}) => {
    // Don't create a new draft if one already exists
    if (hotelId) return hotelId;

    try {
      setLoading(true);
      setError(null);
      const response = await api.createHotelDraft(initialData);
      const id = response.data?.data?.hotelId || response.data?.hotelId;
      setHotelId(id);
      setHotelData(response.data?.data || response.data || {});
      return id;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [hotelId]);

  const fetchDraft = useCallback(async (id) => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.getHotelDraft(id);
      setHotelId(id);
      setHotelData(response.data?.data || response.data || {});
      return response.data?.data || response.data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const saveStep = useCallback(
    async (stepKey, stepData) => {
      if (!hotelId) throw new Error("No hotel draft initialized");
      try {
        setLoading(true);
        setError(null);
        const response = await api.saveHotelStep(hotelId, stepKey, stepData);
        setHotelData((prev) => ({
          ...prev,
          [stepKey]: stepData,
        }));
        return response.data?.data || response.data;
      } catch (err) {
        setError(err.message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [hotelId]
  );

  const submit = useCallback(
    async (confirmationMethod) => {
      if (!hotelId) throw new Error("No hotel draft initialized");
      try {
        setLoading(true);
        setError(null);
        const response = await api.submitHotel(hotelId, confirmationMethod);
        setHotelData((prev) => ({
          ...prev,
          status: "submitted",
          confirmationMethod,
        }));
        return response.data?.data || response.data;
      } catch (err) {
        setError(err.message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [hotelId]
  );

  const uploadDocument = useCallback(
    async (file, type, roomId = null) => {
      if (!hotelId) throw new Error("No hotel draft initialized");
      try {
        setError(null);
        return await api.uploadHotelDocument(hotelId, file, type, roomId);
      } catch (err) {
        setError(err.message);
        throw err;
      }
    },
    [hotelId]
  );

  const deleteDocument = useCallback(
    async (documentId) => {
      if (!hotelId) throw new Error("No hotel draft initialized");
      try {
        setError(null);
        return await api.deleteHotelDocument(hotelId, documentId);
      } catch (err) {
        setError(err.message);
        throw err;
      }
    },
    [hotelId]
  );

  const value = {
    hotelId,
    hotelData,
    loading,
    error,
    initializeDraft,
    fetchDraft,
    saveStep,
    submit,
    uploadDocument,
    deleteDocument,
  };

  return <HotelContext.Provider value={value}>{children}</HotelContext.Provider>;
};

export const useHotel = () => {
  const context = useContext(HotelContext);
  if (!context) {
    throw new Error("useHotel must be used within HotelProvider");
  }
  return context;
};
